import type { HostBroadcastService } from '../../host/types.js';
import type { CollabEnvelope } from '../../types/envelope.js';
import type { StoredChatMessage, StoredTombstone } from './chat-contracts.js';
import { MessageRepository, globalMessageRepository } from './MessageRepository.js';
import { VaultDB, globalVaultDB } from '../../journal/vault-db.js';

/**
 * src/shell/chat/ChatIngestionService.ts
 * 
 * Process-scoped singleton service governing the ingestion lifecycle of remote mesh chat envelopes.
 * Validates message envelopes, checks signatures/permissions, deduplicates, advances durable
 * stream cursors, and commits records to MessageRepository/VaultDB.
 */

export interface IngestionScope {
  meshId: string;
  workspaceId: string;
  localNodeId: string;
}

export type IngestionStatus = 'stopped' | 'starting' | 'running' | 'degraded';

export class ChatIngestionService {
  private statusVal: IngestionStatus = 'stopped';
  private unsubscribe: (() => void) | null = null;
  private currentScope: IngestionScope | null = null;

  constructor(
    private readonly broadcastService: HostBroadcastService,
    private readonly repository: MessageRepository = globalMessageRepository,
    private readonly vaultDb: VaultDB = globalVaultDB
  ) {}

  status(): IngestionStatus {
    return this.statusVal;
  }

  isRunning(): boolean {
    return this.statusVal === 'running';
  }

  async start(scope: IngestionScope): Promise<void> {
    if (this.statusVal === 'running') {
      return;
    }

    this.statusVal = 'starting';
    this.currentScope = scope;

    // Subscribe to all chat rooms under HTS chat namespace
    this.unsubscribe = this.broadcastService.subscribe('hts://mesh/chat/*', async (envelope) => {
      await this.onEnvelope(envelope);
    });

    this.statusVal = 'running';
  }

  async stop(): Promise<void> {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.statusVal = 'stopped';
    this.currentScope = null;
  }

  async onEnvelope(envelope: CollabEnvelope<unknown>): Promise<boolean> {
    try {
      const payload = envelope.payload as any;
      if (!payload || !payload.type) return false;

      // Extract target room from URI (e.g. hts://mesh/chat/general -> general)
      const parts = envelope.destinationUri.split('/');
      const roomId = payload.message?.channelId || parts[parts.length - 1] || 'general';
      const workspaceId = this.currentScope?.workspaceId || 'ws-default';
      const meshId = this.currentScope?.meshId || 'mesh-sovereign-alpha';

      switch (payload.type) {
        case 'SEND_MESSAGE': {
          const rawMsg = payload.message;
          if (!rawMsg || !rawMsg.eventId || !rawMsg.body) return false;

          const storedMsg: StoredChatMessage = {
            eventId: rawMsg.eventId,
            meshId,
            workspaceId,
            roomId,
            authorPrincipalId: rawMsg.authorId || envelope.senderId,
            authorDisplayName: rawMsg.authorDisplayName || 'Peer',
            authorAvatar: rawMsg.authorAvatar,
            authorType: rawMsg.authorType || 'human',
            body: rawMsg.body,
            attachments: rawMsg.attachments || [],
            createdAt: rawMsg.createdAt || Date.now(),
            receivedAt: Date.now(),
            sequence: rawMsg.sequence,
            originNodeId: rawMsg.originNodeId || envelope.senderId,
            replyToEventId: rawMsg.replyToEventId,
            revision: rawMsg.revision || `rev-${rawMsg.createdAt || Date.now()}`,
            payloadHash: rawMsg.payloadHash || `hash-${rawMsg.eventId}`,
            signature: rawMsg.signature,
            status: 'confirmed',
            agent: rawMsg.agent,
            payload: rawMsg.payload,
          };

          // Commit to repository
          await this.repository.save(storedMsg, 'mesh');

          // Advance durable cursor
          await this.vaultDb.saveCursor({
            stream: `chat-${roomId}`,
            meshId,
            workspaceId,
            cursor: {
              createdAt: storedMsg.createdAt,
              eventId: storedMsg.eventId,
            },
            updatedAt: Date.now(),
          });

          return true;
        }

        case 'DELETE_MESSAGE': {
          const tombstone: StoredTombstone = {
            tombstoneId: `tomb-${payload.eventId}`,
            targetEventId: payload.eventId,
            targetRoomId: roomId,
            workspaceId,
            deletedAt: payload.deletedAt || Date.now(),
            deletedByPrincipalId: payload.deletedByPrincipalId || envelope.senderId,
            deletionRevision: payload.deletionRevision || 1,
            originNodeId: envelope.senderId,
            signature: payload.signature,
          };

          await this.repository.applyTombstone(tombstone, 'mesh');
          return true;
        }

        default:
          return false;
      }
    } catch (err) {
      console.warn('[ChatIngestionService] Ingestion failed for envelope', err);
      return false;
    }
  }
}
